import React, { useContext, useMemo, useState } from "react";
import firebase from "firebase";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Paper from "@material-ui/core/Paper";
import { auth } from "../../../base";
import { UserContext } from "../../UserContext";
import openStripeManagementSession from "../../Auth/functions/openStripeManagementSession";

function getPasswordChangeErrorMessage(error) {
	switch (error?.code) {
		case "auth/wrong-password":
			return "Current password is incorrect.";
		case "auth/weak-password":
			return "New password must be at least 6 characters.";
		case "auth/requires-recent-login":
			return "Please log out and log back in, then try again.";
		default:
			return "Unable to change password. Please try again.";
	}
}

export default function MyAccount({ subscriptionStatus }) {
	const { uid, name } = useContext(UserContext);
	const user = auth.currentUser;

	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPasswordForm, setShowPasswordForm] = useState(false);
	const [passwordMessage, setPasswordMessage] = useState(null);
	const [passwordError, setPasswordError] = useState(null);
	const [passwordLoading, setPasswordLoading] = useState(false);
	const [stripeLoading, setStripeLoading] = useState(false);
	const [stripeError, setStripeError] = useState(null);

	const hasPasswordProvider = useMemo(() => user?.providerData?.some((provider) => provider.providerId === "password"), [user]);
	const hasGoogleProvider = useMemo(() => user?.providerData?.some((provider) => provider.providerId === "google.com"), [user]);

	const handlePasswordChange = async (event) => {
		event.preventDefault();
		setPasswordMessage(null);
		setPasswordError(null);

		if (newPassword !== confirmPassword) {
			setPasswordError("New passwords do not match.");
			return;
		}

		if (newPassword.length < 6) {
			setPasswordError("New password must be at least 6 characters.");
			return;
		}

		setPasswordLoading(true);
		try {
			const credential = firebase.auth.EmailAuthProvider.credential(user.email, currentPassword);
			await user.reauthenticateWithCredential(credential);
			await user.updatePassword(newPassword);
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
			setShowPasswordForm(false);
			setPasswordMessage("Password updated successfully.");
		} catch (error) {
			console.error(error);
			setPasswordError(getPasswordChangeErrorMessage(error));
		} finally {
			setPasswordLoading(false);
		}
	};

	const handleCancelPasswordChange = () => {
		setShowPasswordForm(false);
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
		setPasswordError(null);
	};

	const handleManageSubscription = async () => {
		setStripeError(null);
		setStripeLoading(true);
		try {
			await openStripeManagementSession(uid);
		} catch (error) {
			console.error(error);
			setStripeError("Unable to open subscription management. Please try again.");
			setStripeLoading(false);
		}
	};

	return (
		<div>
			<h1 className="page-heading">My Account</h1>

			<h3 className="settings-heading">Profile</h3>
			<Paper className="account-card">
				{name && (
					<div className="account-field">
						<span className="account-field-label">Name</span>
						<span className="account-field-value">{name}</span>
					</div>
				)}
				<div className="account-field">
					<span className="account-field-label">Email</span>
					<span className="account-field-value">{user?.email}</span>
				</div>
			</Paper>

			<h3 className="settings-heading">Password</h3>
			{hasPasswordProvider ? (
				<Paper className="account-card">
					{!showPasswordForm ? (
						<>
							<p className="account-field-note">Use a strong password you do not use elsewhere.</p>
							{passwordMessage && <p className="account-message account-message-success">{passwordMessage}</p>}
							<Button
								variant="outlined"
								color="primary"
								onClick={() => {
									setPasswordMessage(null);
									setShowPasswordForm(true);
								}}>
								Change Password
							</Button>
						</>
					) : (
						<form className="account-password-form" onSubmit={handlePasswordChange}>
							<div className="input-group">
								<label className="account-field-label" htmlFor="current-password">
									Current password
								</label>
								<input
									id="current-password"
									type="password"
									value={currentPassword}
									onChange={(event) => setCurrentPassword(event.target.value)}
									className="signup-input"
									required
								/>
							</div>
							<div className="input-group">
								<label className="account-field-label" htmlFor="new-password">
									New password
								</label>
								<input
									id="new-password"
									type="password"
									value={newPassword}
									onChange={(event) => setNewPassword(event.target.value)}
									className="signup-input"
									required
									minLength={6}
								/>
							</div>
							<div className="input-group">
								<label className="account-field-label" htmlFor="confirm-password">
									Confirm new password
								</label>
								<input
									id="confirm-password"
									type="password"
									value={confirmPassword}
									onChange={(event) => setConfirmPassword(event.target.value)}
									className="signup-input"
									required
									minLength={6}
								/>
							</div>
							{passwordError && <p className="account-message account-message-error">{passwordError}</p>}
							<div className="account-form-actions">
								<Button type="button" onClick={handleCancelPasswordChange} disabled={passwordLoading}>
									Cancel
								</Button>
								<Button type="submit" variant="contained" color="primary" disabled={passwordLoading}>
									{passwordLoading ? "Updating..." : "Update Password"}
								</Button>
							</div>
						</form>
					)}
				</Paper>
			) : (
				<List style={{ maxWidth: "600px", padding: "2px" }} component={Paper}>
					<ListItem>
						{hasGoogleProvider
							? "You sign in with Google. Your password is managed through your Google account."
							: "Password changes are not available for your sign-in method."}
					</ListItem>
				</List>
			)}

			{subscriptionStatus === "active" && (
				<>
					<h3 className="settings-heading">Subscription</h3>
					<List style={{ maxWidth: "600px", padding: "2px" }} component={Paper}>
						<ListItem className="account-subscription-item">
							<div>
								<p>Manage your billing, payment method, and subscription through Stripe.</p>
								{stripeError && <p className="account-message account-message-error">{stripeError}</p>}
								<Button variant="contained" color="primary" onClick={handleManageSubscription} disabled={stripeLoading}>
									{stripeLoading ? "Opening..." : "Manage Subscription"}
								</Button>
							</div>
						</ListItem>
					</List>
				</>
			)}
		</div>
	);
}
